/**
 * Walks of the asset-type inheritance chain.
 *
 * Asset types form a tree (`hvac_boiler` → `trade_hvac` → `equipment`), and
 * several UI decisions depend on an ancestor rather than on the leaf: whether a
 * quantity applies, whether the Photo tab applies. The walk was copy-pasted
 * per-decision inside AssetDetailPage; it lives here so the page and the hooks
 * that need the same answer cannot drift.
 */

/** Minimal shape both callers already have from `useAssetTypes`. */
export type AssetTypeNode = {
    uid?: string
    code?: string
    properties?: Record<string, unknown> | null
    inherits_from_asset_type_uid?: string | null
}

function walkUp<T>(
    assetTypes: AssetTypeNode[],
    assetTypeUid: string | undefined | null,
    visit: (node: AssetTypeNode) => T | undefined,
): T | undefined {
    if (!assetTypeUid) return undefined
    const byUid = new Map(assetTypes.map((at) => [at.uid, at]))
    let current = byUid.get(assetTypeUid)
    // Guard against a cycle in the data rather than hanging the render.
    const seen = new Set<string>()
    while (current) {
        const hit = visit(current)
        if (hit !== undefined) return hit
        const parentUid = current.inherits_from_asset_type_uid
        if (!parentUid || seen.has(parentUid)) return undefined
        seen.add(parentUid)
        current = byUid.get(parentUid)
    }
    return undefined
}

/**
 * True when the type — or any ancestor — declares `properties.multiple`.
 * Mirrors the backend's `assetTypeAllowsMultiple`, so the quantity field is
 * offered exactly when the API will accept `quantity > 1`.
 */
export function assetTypeAllowsMultiple(
    assetTypes: AssetTypeNode[],
    assetTypeUid: string | undefined | null,
): boolean {
    return walkUp(assetTypes, assetTypeUid, (node) => {
        const props = node.properties as { multiple?: boolean } | undefined | null
        return props?.multiple === true ? true : undefined
    }) ?? false
}

/** True when the type is `ancestorCode` or descends from it. */
export function assetTypeDescendsFrom(
    assetTypes: AssetTypeNode[],
    assetTypeUid: string | undefined | null,
    ancestorCode: string,
): boolean {
    return walkUp(assetTypes, assetTypeUid, (node) =>
        node.code === ancestorCode ? true : undefined,
    ) ?? false
}

/**
 * `assetTypeDescendsFrom` keyed by the type's CODE rather than its uid —
 * the twin of the backend's `AssetTypeCodeInheritsFrom`.
 *
 * Both plan gates (the Plan tab, and whether the viewer may ask for an
 * asset-centric scene) hold a type code and nothing else, and a code-set
 * membership test is exactly what spec 2026-09-19-02 replaces: it drops every
 * `room_*` sub-type the ontology says inherits from `room`. An unknown code is
 * simply not a descendant.
 */
export function assetTypeCodeDescendsFrom(
    assetTypes: AssetTypeNode[],
    assetTypeCode: string | undefined | null,
    ancestorCode: string,
): boolean {
    if (!assetTypeCode) return false
    if (assetTypeCode === ancestorCode) return true
    const node = assetTypes.find((at) => at.code === assetTypeCode)
    return assetTypeDescendsFrom(assetTypes, node?.uid, ancestorCode)
}
